import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import type { NotificationPort } from '@src/modules/platform/notifications/domain/ports/NotificationPort.ts';

const PERMISSION_RESUME_HINT =
  'Si activó permisos en Ajustes, vuelva a pulsar Actualizar GPS o Tomar foto.';

let permissionDeniedThisSession = false;
let resumeListenerBound = false;

export const GPS_PERMISSION_DENIED_MESSAGE =
  'Ubicación bloqueada. Abra Ajustes → Aplicaciones → Paquetes → Permisos y active Ubicación.';

export const CAMERA_PERMISSION_DENIED_MESSAGE =
  'Cámara bloqueada. Abra Ajustes → Aplicaciones → Paquetes → Permisos y active Cámara.';

const SETTINGS_SUFFIX = Capacitor.isNativePlatform()
  ? ''
  : ' También puede revisar los permisos del navegador.';

function markPermissionDeniedThisSession(): void {
  permissionDeniedThisSession = true;
}

export function notifyGpsPermissionDenied(notifications: NotificationPort): void {
  markPermissionDeniedThisSession();
  notifications.warning(`${GPS_PERMISSION_DENIED_MESSAGE}${SETTINGS_SUFFIX}`);
}

export function notifyCameraPermissionDenied(notifications: NotificationPort): void {
  markPermissionDeniedThisSession();
  notifications.warning(`${CAMERA_PERMISSION_DENIED_MESSAGE}${SETTINGS_SUFFIX}`);
}

/** One info toast after returning from Android Settings when a permission was denied earlier. */
export function installCapacitorPermissionResumeHint(notifications: NotificationPort): () => void {
  if (!Capacitor.isNativePlatform() || resumeListenerBound) return () => {};
  resumeListenerBound = true;
  let remove: (() => void) | undefined;
  void App.addListener('resume', () => {
    if (!permissionDeniedThisSession) return;
    permissionDeniedThisSession = false;
    notifications.info(PERMISSION_RESUME_HINT);
  }).then((handle) => {
    remove = () => void handle.remove();
  });

  const onNativeToast = (ev: Event): void => {
    const raw = (ev as CustomEvent<unknown>).detail;
    let message = 'No se pudo cargar la app.';
    if (typeof raw === 'string' && raw.trim()) {
      try {
        const parsed = JSON.parse(raw) as { message?: string };
        if (typeof parsed.message === 'string' && parsed.message.trim()) message = parsed.message.trim();
        else message = raw.trim();
      } catch {
        message = raw.trim();
      }
    } else if (raw && typeof raw === 'object' && 'message' in raw) {
      const m = (raw as { message?: unknown }).message;
      if (typeof m === 'string' && m.trim()) message = m.trim();
    }
    if (ev.type === 'paquetes:permission-denied') {
      markPermissionDeniedThisSession();
      notifications.warning(message);
      return;
    }
    notifications.error(message);
  };
  globalThis.addEventListener('paquetes:webview-error', onNativeToast);
  globalThis.addEventListener('paquetes:permission-denied', onNativeToast);

  return () => {
    resumeListenerBound = false;
    permissionDeniedThisSession = false;
    remove?.();
    globalThis.removeEventListener('paquetes:webview-error', onNativeToast);
    globalThis.removeEventListener('paquetes:permission-denied', onNativeToast);
  };
}

export function resetCapacitorPermissionHintsForTests(): void {
  permissionDeniedThisSession = false;
  resumeListenerBound = false;
}

export async function isCameraPermissionDenied(): Promise<boolean> {
  const permissions = globalThis.navigator?.permissions;
  if (!permissions?.query) return false;
  try {
    const status = await permissions.query({ name: 'camera' as PermissionName });
    return status.state === 'denied';
  } catch {
    return false;
  }
}

/** Returns false when capture must be blocked (permission denied). */
export async function guardCameraCapture(onDenied?: () => void): Promise<boolean> {
  if (!(await isCameraPermissionDenied())) return true;
  onDenied?.();
  return false;
}
